// 연속 처치(폭주) HUD의 순수 계산(COMBAT-FEEL 2.5). DOM 없이 view.rampage를 화면 값으로 바꾼다. 시험은 rampageModel.test.ts.
// 값은 전부 작은 정수로 양자화해 두어, HUD가 바뀐 프레임에만 DOM을 쓰게 한다(막대는 3초 동안 최대 WINDOW_STEPS번).
import { RAMPAGE, STYLE_RANK } from '../shared/config';

/** 사슬 창 막대 단계 수: 3초 창을 5% 단위로 */
export const WINDOW_STEPS = 20;

export interface RampageHudState {
  /** 보일지: 전투 중이고 사슬이 이어지는 동안(사슬 ≥ 1, 창 남음) */
  visible: boolean;
  /** 현재 사슬 수 */
  chain: number;
  /** 남은 창 막대 0..WINDOW_STEPS(창이 남았으면 최소 1) */
  windowStep: number;
  /** 다음 보상까지 채운 칸 수 0..every. 5의 배수에 막 도달했으면 every(가득) */
  pips: number;
  /** 다음 보상까지 남은 처치 수 1..every */
  next: number;
  /** 이동 속도 효과가 붙어 있는지 */
  buffOn: boolean;
  /** 스타일 랭크 0..STYLE_RANK.letters.length(보이지 않으면 0, COMBAT-FEEL 2.14) */
  rank: number;
}

export interface RampageLike { readonly chain: number; readonly timer: number; readonly buff: number; readonly rank?: number }

/** 남은 창 틱 → 0..steps 단계(남았으면 최소 1, 없으면 0). 창 틱은 RAMPAGE.window(180)이 가득 */
export function windowStep(timer: number, window: number = RAMPAGE.window, steps: number = WINDOW_STEPS): number {
  if (!(timer > 0) || !(window > 0)) return 0;
  return Math.max(1, Math.min(steps, Math.ceil((timer / window) * steps)));
}

/** 다음 보상(5마리마다)까지의 진행: chain 12 → 채운 2칸, 남은 3. 5·10처럼 배수면 가득(5칸)이고 다음 보상까지 5 */
export function rewardProgress(chain: number, every: number = RAMPAGE.every): { pips: number; next: number } {
  const c = Math.max(0, Math.floor(chain));
  if (c === 0) return { pips: 0, next: every };
  const r = c % every;
  return { pips: r === 0 ? every : r, next: every - r };
}

/** 같은 객체를 갱신해 돌려준다(할당 없음). live = 던전 전투 중 */
export function rampageHudState(out: RampageHudState, r: RampageLike, live: boolean): RampageHudState {
  const chain = Math.max(0, Math.floor(r.chain));
  const ws = windowStep(r.timer);
  out.visible = live && chain > 0 && ws > 0;
  out.chain = out.visible ? chain : 0;
  out.windowStep = out.visible ? ws : 0;
  // rewardProgress()는 새 객체를 만들므로 매 프레임 경로에서는 같은 계산을 스칼라로 한다(프레임 루프 할당 금지)
  const every = RAMPAGE.every, r5 = out.chain % every;
  out.pips = out.chain === 0 ? 0 : r5 === 0 ? every : r5;
  out.next = out.chain === 0 ? every : every - r5;
  out.buffOn = out.visible && r.buff > 0;
  out.rank = out.visible ? Math.max(0, Math.min(STYLE_RANK.letters.length, Math.floor(r.rank ?? 0))) : 0;
  return out;
}

export const newRampageHudState = (): RampageHudState => ({ visible: false, chain: 0, windowStep: 0, pips: 0, next: RAMPAGE.every, buffOn: false, rank: 0 });

/** 두 상태가 화면에서 같은가(바뀐 값만 DOM에 쓰려는 비교) */
export function sameRampageState(a: RampageHudState, b: RampageHudState): boolean {
  return a.visible === b.visible && a.chain === b.chain && a.windowStep === b.windowStep && a.pips === b.pips && a.next === b.next && a.buffOn === b.buffOn && a.rank === b.rank;
}

/** 스타일 랭크 글자(0이면 빈 문자열) */
export function styleRankLetter(rank: number): string {
  return rank >= 1 && rank <= STYLE_RANK.letters.length ? STYLE_RANK.letters[rank - 1] : '';
}
